import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { API_BASE } from '../config/api'
import { useCountUp } from '../hooks/useCountUp'
import { useReveal } from '../hooks/useReveal'
import BentoStream from '../components/sections/BentoStream'
import Marquee from '../components/sections/Marquee'
import ClientWork from '../components/sections/ClientWork'
import Reviews from '../components/sections/Reviews'
import ProjectCard from '../components/cards/ProjectCard'

// Import service illustrations
import { MobileAppIllustration, WebIllustration, ApiIllustration, BackendIllustration } from '../components/sections/BentoIllustrations'

/* =============================================
   Types & Defaults (API data structures unchanged)
   ============================================= */

interface HeroContent {
  title: { line1: string; line2: string }
  subtitle: string
  stats: Array<{ number: string; label: string }>
  ctaButtons: Array<{ text: string; link: string; primary: boolean }>
}

interface TeamMember {
  id: string
  name?: string
  role?: string
  avatar?: string
}

const defaultHeroContent: HeroContent = {
  title: { line1: 'Ionic Errrrs', line2: 'Code' },
  subtitle: 'A sophisticated development team creating beautiful digital experiences with cutting-edge technologies and innovative solutions.',
  stats: [
    { number: '50+', label: 'Projects Completed' },
    { number: '25+', label: 'Happy Clients' },
    { number: '5+', label: 'Years Experience' },
    { number: '10+', label: 'Technologies' },
  ],
  ctaButtons: [
    { text: 'View Our Work', link: '/projects', primary: true },
    { text: 'Meet the Team', link: '/team', primary: false },
  ],
}

const defaultServices = [
  { icon: '▣', title: 'Mobile App Development', description: 'High-performance cross-platform mobile apps built with Flutter and React Native for real users.', variant: 'feature' as const, gridClasses: 'col-span-1 sm:col-span-2 row-span-1 sm:row-span-2', illustration: MobileAppIllustration },
  { icon: '◑', title: 'Web Development', description: 'Modern, responsive web apps with great UX using React, Next.js, and TypeScript.', variant: 'default' as const, gridClasses: 'col-span-1 sm:col-span-2 row-span-1', illustration: WebIllustration },
  { icon: '◦', title: 'API Development', description: 'Clean, secure APIs designed for scale and speed.', variant: 'default' as const, gridClasses: 'col-span-1 row-span-1', illustration: ApiIllustration },
  { icon: '▤', title: 'Backend Development', description: 'Reliable backends with solid architecture and performance.', variant: 'default' as const, gridClasses: 'col-span-1 row-span-1', illustration: BackendIllustration },
]

/* =============================================
   Stat Counter Component
   ============================================= */
function StatCell({ target, label }: { target: number; label: string }) {
  const { ref, displayValue } = useCountUp(target)
  return (
    <div ref={ref} className="text-center py-6 px-4">
      <div className="stat-number">{displayValue}</div>
      <div className="stat-label">{label}</div>
    </div>
  )
}

/* =============================================
   Helper: parse "50+" → 50
   ============================================= */
function parseStatNumber(str: string): number {
  const n = parseInt(str.replace(/[^0-9]/g, ''), 10)
  return isNaN(n) ? 0 : n
}

/* =============================================
   Helper: check if avatar is an image URL
   ============================================= */
const isImageUrl = (avatar: unknown): avatar is string => {
  if (typeof avatar !== 'string') return false
  const str = avatar.trim()
  if (!str) return false
  return str.startsWith('http') || str.startsWith('/')
}

/* =============================================
   Helper: get initials from name
   ============================================= */
function getInitials(name?: string): string {
  if (!name) return '??'
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

/* =============================================
   Main Home Component
   ============================================= */
export default function Home() {
  const [heroContent, setHeroContent] = useState<HeroContent>(defaultHeroContent)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [teamLoading, setTeamLoading] = useState(true)
  const [recentProjects, setRecentProjects] = useState<any[]>([])
  const [projectsLoading, setProjectsLoading] = useState(true)

  // Reveal refs for sections
  const heroReveal = useReveal<HTMLDivElement>()
  const servicesReveal = useReveal<HTMLDivElement>()
  const projectsReveal = useReveal<HTMLDivElement>()
  const teamReveal = useReveal<HTMLDivElement>()

  /* ---- API: Fetch hero content (stats) ---- */
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [heroRes] = await Promise.all([
          fetch(`${API_BASE}/api/v1/content/hero`),
        ])

        const heroData = await heroRes.json()

        if (heroData.success && heroData.data) {
          setHeroContent((current) => ({
            ...current,
            stats: heroData.data.stats ?? current.stats,
          }))
        }
      } catch (error) {
        console.error('Failed to fetch hero content:', error)
      }
    }

    fetchContent()
  }, [])

  /* ---- API: Fetch team members ---- */
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/v1/team`)
        if (!response.ok) throw new Error(`Team API failed: ${response.status}`)
        const data = await response.json()
        const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
        setTeamMembers(list.map((m: unknown, i: number) => {
          const obj = (m ?? {}) as Record<string, unknown>
          return {
            id: typeof obj.id === 'string' && obj.id ? obj.id : `team-${i}`,
            name: typeof obj.name === 'string' ? obj.name : undefined,
            role: typeof obj.role === 'string' ? obj.role : undefined,
            avatar: typeof obj.avatar === 'string' && obj.avatar ? obj.avatar : undefined,
            isCoreTeamMember: typeof obj.isCoreTeamMember === 'boolean'
              ? obj.isCoreTeamMember
              : obj.isCoreTeamMember === undefined ? true : (obj.isCoreTeamMember === 'true' || obj.isCoreTeamMember === 1 || obj.isCoreTeamMember === '1'),
          }
        }))
      } catch (error) {
        console.error('Failed to fetch team:', error)
      } finally {
        setTeamLoading(false)
      }
    }
    fetchTeam()
  }, [])

  /* ---- API: Fetch recent projects ---- */
  useEffect(() => {
    const fetchRecentProjects = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/v1/projects`)
        if (!response.ok) throw new Error(`Projects API failed: ${response.status}`)
        const data = await response.json()
        const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])

        // Take the first 3 projects (most recent)
        setRecentProjects(list.slice(0, 3).map((p: any, i: number) => {
          const obj = (p ?? {}) as Record<string, any>
          return {
            id: typeof obj.id === 'string' && obj.id ? obj.id : `project-${i}`,
            title: typeof obj.title === 'string' ? obj.title : 'Untitled Project',
            description: typeof obj.description === 'string' ? obj.description : '',
            image: typeof obj.image === 'string' ? obj.image : '📱',
            technologies: Array.isArray(obj.technologies) ? obj.technologies : [],
            status: typeof obj.status === 'string' ? obj.status : 'Active',
            duration: typeof obj.duration === 'string' ? obj.duration : 'Timeline TBD',
            team: typeof obj.team === 'string' ? obj.team : 'Team TBD',
          }
        }))
      } catch (error) {
        console.error('Failed to fetch recent projects:', error)
      } finally {
        setProjectsLoading(false)
      }
    }
    fetchRecentProjects()
  }, [])

  return (
    <div>
      <Helmet>
        <title>Ionic Errrrs Code — We craft digital experiences</title>
        <meta name="description" content="Ionic Errrrs Code is a development team building high-performance mobile apps, web apps, APIs and backends." />
        <link rel="canonical" href="https://ionicerrrrscode.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Ionic Errrrs Code" />
        <meta property="og:title" content="Ionic Errrrs Code — We craft digital experiences" />
        <meta property="og:description" content="Ionic Errrrs Code is a development team building high-performance mobile apps, web apps, APIs and backends." />
        <meta property="og:url" content="https://ionicerrrrscode.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ionic Errrrs Code — We craft digital experiences" />
        <meta name="twitter:description" content="Ionic Errrrs Code is a development team building high-performance mobile apps, web apps, APIs and backends." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Ionic Errrrs Code",
            "url": "https://ionicerrrrscode.com",
            "description": "A development team building high-performance mobile apps, web apps, APIs and backends.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": { "@type": "EntryPoint", "urlTemplate": "https://ionicerrrrscode.com/projects?q={search_term_string}" },
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>

      {/* ========== 1. HERO ========== */}
      <section className="hero-section">
        <div className="container-page">
          <div
            ref={heroReveal.ref}
            className="hero-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.08fr .92fr',
              gap: '60px',
              alignItems: 'center',
              opacity: heroReveal.isVisible ? 1 : 0,
              transform: heroReveal.isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity .7s var(--ease), transform .7s var(--ease)',
            }}
          >
            {/* Left column */}
            <div>
              {/* Badges */}
              <div className="flex flex-wrap gap-2.5 mb-6">
                <span className="badge">
                  <span className="badge-dot" />
                  Available for new projects
                </span>
                <span className="badge">25+ happy clients</span>
              </div>

              {/* H1 */}
              <h1 style={{ color: 'var(--text)' }}>
                We craft digital experiences that inspire and innovate.
              </h1>

              {/* Lead paragraph */}
              <p className="mt-6 text-[1.16rem] leading-relaxed" style={{ color: 'var(--muted)', maxWidth: '500px' }}>
                A passionate team shipping high-performance mobile apps, web apps, APIs, and backend systems with cutting-edge technologies.
              </p>

              {/* CTA buttons */}
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/projects" className="btn-pill btn-primary no-underline">
                  View our work
                </Link>
                <Link to="/start-project" className="btn-pill btn-ghost no-underline">
                  Start a project
                </Link>
              </div>
            </div>

            {/* Right column — Hero visual + Bento Stream */}
            <div className="hero-visual">
              <BentoStream />
            </div>
          </div>
        </div>
      </section>

      {/* ========== 2. MARQUEE ========== */}
      <Marquee />

      {/* ========== 3. STATS BAR ========== */}
      <section className="stats-section">
        <div className="container-page">
          <div className="stats-bar rounded-card overflow-hidden">
            {heroContent.stats.map((stat) => (
              <div
                key={stat.label}
                className="stats-bar-item"
              >
                <StatCell target={parseStatNumber(stat.number)} label={stat.label} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 4. SERVICES — BENTO GRID ========== */}
      <section className="section-spacing" id="services">
        <div className="container-page">
          <div className="section-head">
            <span className="eyebrow">Services</span>
            <h2 className="mt-4" style={{ color: 'var(--text)' }}>What we do</h2>
            <p className="mt-4 text-[1.06rem]" style={{ color: 'var(--muted)' }}>
              From concept to deployment, we deliver comprehensive digital solutions.
            </p>
          </div>

          <div
            ref={servicesReveal.ref}
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 auto-rows-[198px] gap-[18px]"
            style={{
              opacity: servicesReveal.isVisible ? 1 : 0,
              transform: servicesReveal.isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity .7s var(--ease), transform .7s var(--ease)',
            }}
          >
            {defaultServices.map((service) => {
              // 2x2 Feature Card (Mobile App Dev) — Card colors/design UNTOUCHED
              if (service.variant === 'feature') {
                return (
                  <div
                    key={service.title}
                    className="card-inverse col-span-1 sm:col-span-2 row-span-1 sm:row-span-2 group relative overflow-hidden p-5 sm:p-6"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    {service.illustration && (
                      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 overflow-hidden select-none" style={{ minHeight: '140px' }}>
                        <div className="h-[170px] w-full flex items-center justify-center transition-transform duration-500 group-hover:scale-[1.03]" style={{ opacity: 0.9 }}>
                          <service.illustration />
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="icon-chip mb-4">{service.icon}</div>
                      <h3 className="font-display text-[1.22rem] font-semibold mb-2" style={{ color: 'var(--inverse-text)' }}>
                        {service.title}
                      </h3>
                      <p className="text-sm leading-relaxed" style={{ color: 'var(--inverse-text)', opacity: 0.7 }}>
                        {service.description}
                      </p>
                    </div>
                  </div>
                )
              }

              // 2x1 Horizontal Card (Web Dev) — Card colors/design UNTOUCHED, side-aligned illustration
              if (service.title === 'Web Development') {
                return (
                  <div
                    key={service.title}
                    className="card-studio col-span-1 sm:col-span-2 row-span-1 group relative overflow-hidden p-5 sm:p-6"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <div className="flex flex-col sm:flex-row items-stretch justify-between h-full gap-6 w-full relative">
                      <div className="flex-1 flex flex-col justify-end relative z-10">
                        <div className="icon-chip mb-4">{service.icon}</div>
                        <h3 className="font-display text-[1.22rem] font-semibold mb-2" style={{ color: 'var(--text)' }}>
                          {service.title}
                        </h3>
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>
                          {service.description}
                        </p>
                      </div>
                      {service.illustration && (
                        <div className="flex items-center justify-center w-full sm:w-2/5 p-2 select-none z-10" style={{ minHeight: '80px' }}>
                          <div className="h-[110px] sm:h-[120px] w-full flex items-center justify-center transition-transform duration-500 group-hover:scale-[1.03]" style={{ opacity: 0.95 }}>
                            <service.illustration />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              }

              // 1x1 Cards (API and Backend) — Card colors/design UNTOUCHED, illustration shown properly (top-aligned)
              return (
                <div
                  key={service.title}
                  className="card-studio col-span-1 row-span-1 group relative overflow-hidden p-5 flex flex-col justify-between"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  {service.illustration && (
                    <div className="flex-1 flex items-center justify-center p-1 select-none overflow-hidden" style={{ minHeight: '80px' }}>
                      <div className="h-[75px] w-full flex items-center justify-center transition-transform duration-500 group-hover:scale-[1.05]" style={{ opacity: 0.95 }}>
                        <service.illustration />
                      </div>
                    </div>
                  )}
                  <div className="mt-2">
                    <h3 className="font-display text-[1.15rem] font-semibold mb-1" style={{ color: 'var(--text)' }}>
                      {service.title}
                    </h3>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--muted)' }}>
                      {service.description}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ========== 5. PROJECTS (placeholder cards — links to /projects) ========== */}
      <section className="section-spacing" id="projects">
        <div className="container-page">
          <div className="section-head">
            <span className="eyebrow">Projects</span>
            <h2 className="mt-4" style={{ color: 'var(--text)' }}>Our recent work</h2>
            <p className="mt-4 text-[1.06rem]" style={{ color: 'var(--muted)' }}>
              A selection of projects we've shipped for clients and for ourselves.
            </p>
          </div>

          <div
            ref={projectsReveal.ref}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[22px]"
            style={{
              opacity: projectsReveal.isVisible ? 1 : 0,
              transform: projectsReveal.isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity .7s var(--ease), transform .7s var(--ease)',
            }}
          >
            {projectsLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="shimmer-card" style={{ height: '480px' }}>
                  <div>
                    {/* Header skeleton */}
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 rounded-lg shimmer-item flex-shrink-0" />
                      <div className="flex-1 space-y-2 mt-1">
                        <div className="h-4 w-3/4 rounded shimmer-item" />
                        <div className="h-3 w-1/3 rounded shimmer-item" />
                      </div>
                    </div>
                    {/* Description skeleton */}
                    <div className="space-y-2 mb-6">
                      <div className="h-3 w-full rounded shimmer-item" />
                      <div className="h-3 w-full rounded shimmer-item" />
                      <div className="h-3 w-2/3 rounded shimmer-item" />
                    </div>
                    {/* Tags skeleton */}
                    <div className="flex gap-2 mb-6">
                      <div className="h-6 w-16 rounded-full shimmer-item" />
                      <div className="h-6 w-20 rounded-full shimmer-item" />
                    </div>
                  </div>
                  {/* Footer skeleton */}
                  <div className="flex justify-between items-center pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                    <div className="h-3 w-24 rounded shimmer-item" />
                    <div className="h-3 w-16 rounded shimmer-item" />
                  </div>
                </div>
              ))
            ) : recentProjects.length > 0 ? (
              recentProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))
            ) : (
              <div className="col-span-full text-center py-8">
                <p style={{ color: 'var(--muted)' }}>No projects found.</p>
              </div>
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/projects" className="btn-pill btn-ghost no-underline">
              View all projects →
            </Link>
          </div>
        </div>
      </section>

      {/* ========== 6. CLIENT WORK ========== */}
      <ClientWork />

      {/* ========== 7. REVIEWS ========== */}
      <Reviews />

      {/* ========== 8. TEAM ========== */}
      <section className="section-spacing" id="team">
        <div className="container-page">
          <div className="section-head">
            <span className="eyebrow">Team</span>
            <h2 className="mt-4" style={{ color: 'var(--text)' }}>The people behind the code</h2>
          </div>

          <div
            ref={teamReveal.ref}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
            style={{
              opacity: teamReveal.isVisible ? 1 : 0,
              transform: teamReveal.isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity .7s var(--ease), transform .7s var(--ease)',
            }}
          >
            {teamLoading ? (
              // Shimmer placeholders
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="shimmer-card text-center flex flex-col items-center justify-center" style={{ padding: '2rem', height: '240px' }}>
                  <div className="w-[88px] h-[88px] rounded-full shimmer-item mb-4" />
                  <div className="h-4 w-3/4 rounded shimmer-item mb-2" />
                  <div className="h-3 w-1/2 rounded shimmer-item" />
                </div>
              ))
            ) : (
              teamMembers.filter((m: any) => m.isCoreTeamMember !== false).map((member) => (
                <Link
                  key={member.id}
                  to={`/team/${member.id}`}
                  className="card-studio text-center no-underline"
                  style={{ padding: '2rem' }}
                >
                  {/* Avatar */}
                  <div className="avatar-initials mb-4">
                    {isImageUrl(member.avatar) ? (
                      <img
                        src={member.avatar}
                        alt={member.name || 'Team member'}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          target.parentElement!.textContent = getInitials(member.name)
                        }}
                      />
                    ) : (
                      getInitials(member.name)
                    )}
                  </div>
                  <h3 className="font-display text-[1.06rem] font-semibold" style={{ color: 'var(--text)' }}>
                    {member.name || 'Team Member'}
                  </h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                    {member.role || 'Developer'}
                  </p>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      {/* ========== 9. CTA ========== */}
      <section className="section-spacing" id="contact">
        <div className="container-page">
          <div className="cta-block">
            <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3.2rem)' }}>
              Ready to build something amazing?
            </h2>
            <p className="mt-4 text-[1.06rem] mx-auto" style={{ maxWidth: '500px', color: 'var(--inverse-text)', opacity: 0.6 }}>
              Let's turn your ideas into reality. We'd love to hear about your next project.
            </p>
            <div className="mt-8">
              <a href="mailto:hello@ionicerrrrscode.com" className="btn-pill btn-inverse no-underline">
                Get in touch
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}